import { cellToBoundary, cellToLatLng, latLngToCell, gridDisk } from 'h3-js';
import { union } from 'polyclip-ts';

export function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function getPlaceHexagons(place) {
  if (!place) return [];
  const hexes = place.h3Indexes || place.h3_indexes || [];
  if (hexes.length > 0) return hexes;
  const center = getPlaceCenter(place);
  if (!center) return [];
  try {
    const centerCell = latLngToCell(center[0], center[1], 8);
    return gridDisk(centerCell, 2);
  } catch (err) {
    return [];
  }
}

export const getPlaceCenter = (place) => {
  if (!place) return null;
  if (place.lat != null && place.lng != null) {
    return [place.lat, place.lng];
  }
  const hexes = place.h3Indexes || place.h3_indexes || [];
  if (hexes.length === 0) return null;
  if (hexes.length === 1) {
    return cellToLatLng(hexes[0]);
  }
  const sample = hexes.slice(0, 15);
  let sumLat = 0, sumLng = 0;
  sample.forEach(hex => {
    const [lat, lng] = cellToLatLng(hex);
    sumLat += lat;
    sumLng += lng;
  });
  return [sumLat / sample.length, sumLng / sample.length];
};

/**
 * Generates an organic, human-like drawn circle (closed loop and overlapping pen stroke)
 * around a geographic center coordinate.
 */
export function getHumanDrawnCircle(center, radiusKm, seedKey = '', numPoints = 48) {
  let hash = 0;
  for (let i = 0; i < seedKey.length; i++) {
    hash = ((hash << 5) - hash) + seedKey.charCodeAt(i);
    hash |= 0;
  }
  const pseudoRand = (offset) => {
    const x = Math.sin((hash + offset) * 9301 + 49297) * 233280;
    return x - Math.floor(x);
  };

  // Human hand imperfections:
  // 1. Natural tilt and oblong ratio (humans draw gentle ovals, not mechanical circles)
  const tilt = pseudoRand(1) * Math.PI;
  const stretch = 1.08 + pseudoRand(2) * 0.16; // 1.08 to 1.24 ratio
  
  // 2. Multi-frequency organic waviness
  const wobble1 = 0.035 + pseudoRand(3) * 0.045;
  const phase1 = pseudoRand(4) * 2 * Math.PI;
  const wobble2 = 0.015 + pseudoRand(5) * 0.035;
  const phase2 = pseudoRand(6) * 2 * Math.PI;

  const [centerLat, centerLng] = center;
  const kmPerLat = 111.0;
  const kmPerLng = 111.0 * Math.max(Math.cos(centerLat * Math.PI / 180), 0.01);

  // Exact closed loop for cutout hole in the inverted polygon
  const closedHole = [];
  for (let i = 0; i < numPoints; i++) {
    const theta = (2 * Math.PI * i) / numPoints;
    const tRotated = theta - tilt;
    const rOval = radiusKm * (1.0 + (stretch - 1.0) * (Math.cos(tRotated) ** 2));
    const r = rOval * (1.0 + wobble1 * Math.sin(2 * theta + phase1) + wobble2 * Math.cos(3 * theta + phase2));
    
    const dLat = (r * Math.sin(theta)) / kmPerLat;
    const dLng = (r * Math.cos(theta)) / kmPerLng;
    closedHole.push([centerLat + dLat, centerLng + dLng]);
  }
  closedHole.push(closedHole[0]); // Ensure perfectly closed polygon ring

  // Overlapping pen stroke (continues ~45 degrees past 360 with a natural subtle drift)
  const drawnStroke = [];
  const strokePoints = numPoints + 6;
  for (let i = 0; i < strokePoints; i++) {
    const theta = (2 * Math.PI * (i % numPoints)) / numPoints;
    const tRotated = theta - tilt;
    const rOval = radiusKm * (1.0 + (stretch - 1.0) * (Math.cos(tRotated) ** 2));
    const overlapDrift = i >= numPoints ? 1.0 + (i - numPoints) * 0.008 * (pseudoRand(7) > 0.5 ? 1 : -1) : 1.0;
    const r = rOval * overlapDrift * (1.0 + wobble1 * Math.sin(2 * theta + phase1) + wobble2 * Math.cos(3 * theta + phase2));
    
    const dLat = (r * Math.sin(theta)) / kmPerLat;
    const dLng = (r * Math.cos(theta)) / kmPerLng;
    drawnStroke.push([centerLat + dLat, centerLng + dLng]);
  }

  return { closedHole, drawnStroke };
}

/**
 * Calculates a human-drawn circle that comfortably surrounds all hexagons of a place.
 */
export function getPlaceCircle(place, visitedHexes = {}) {
  const center = getPlaceCenter(place);
  if (!center) return null;

  let maxDist = 0;
  const [cLat, cLng] = center;
  const hexes = place.h3Indexes || place.h3_indexes || [];
  if (hexes.length > 0) {
    hexes.forEach(hex => {
      const boundary = visitedHexes[hex] || cellToBoundary(hex);
      boundary.forEach(([lat, lng]) => {
        const d = haversineKm(cLat, cLng, lat, lng);
        if (d > maxDist) maxDist = d;
      });
    });
  }

  // Snug margin around outermost hexagon edge:
  // If no hexes (unvisited bucket list pin), default to a clean 4.5 km circle
  // For tight address-level places, minimum radius is 0.75 km (~750m)
  // For larger towns and cities, scale naturally with minimum 5.0 km
  let radiusKm;
  if (maxDist === 0) {
    radiusKm = 4.5;
  } else if (maxDist < 0.8) {
    radiusKm = Math.max(maxDist * 1.35, 0.75);
  } else if (maxDist < 2.5) {
    radiusKm = Math.max(maxDist * 1.25, 1.4);
  } else {
    radiusKm = Math.max(maxDist * 1.22, 5.0);
  }

  const circleData = getHumanDrawnCircle(center, radiusKm, place.name || place.id || 'place');
  return {
    place,
    center,
    radiusKm,
    closedHole: circleData.closedHole,
    drawnStroke: circleData.drawnStroke
  };
}

export function generateAllPlaceCircles(places = [], visitedHexes = {}, statusFilter = 'visited') {
  const results = [];
  places.forEach(place => {
    if (statusFilter && statusFilter !== 'all') {
      const isVisited = place.status !== 'yet_to_visit';
      if (statusFilter === 'visited' && !isVisited) return;
      if (statusFilter === 'yet_to_visit' && isVisited) return;
    }
    const circle = getPlaceCircle(place, visitedHexes);
    if (circle) {
      results.push(circle);
    }
  });
  return results;
}

/**
 * Merges overlapping circles into seamless continuous regions using boolean polygon clipping.
 * Avoids any overlapping holes in Leaflet inverted polygons, eliminating opacity-flip artifacts.
 */
export function getUnionedCircleRegions(placeCircles = []) {
  if (!placeCircles || placeCircles.length === 0) {
    return { holes: [], strokes: [] };
  }
  if (placeCircles.length === 1) {
    return {
      holes: [placeCircles[0].closedHole],
      strokes: [placeCircles[0].drawnStroke]
    };
  }

  // Fast disjoint-set (union-find) clustering by distance
  const n = placeCircles.length;
  const parent = Array.from({ length: n }, (_, i) => i);
  function find(i) {
    if (parent[i] === i) return i;
    return (parent[i] = find(parent[i]));
  }
  function unionSets(i, j) {
    const rootI = find(i);
    const rootJ = find(j);
    if (rootI !== rootJ) parent[rootI] = rootJ;
  }

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const c1 = placeCircles[i];
      const c2 = placeCircles[j];
      const dist = haversineKm(c1.center[0], c1.center[1], c2.center[0], c2.center[1]);
      if (dist <= (c1.radiusKm + c2.radiusKm) * 1.05) {
        unionSets(i, j);
      }
    }
  }

  const clusters = {};
  for (let i = 0; i < n; i++) {
    const root = find(i);
    if (!clusters[root]) clusters[root] = [];
    clusters[root].push(placeCircles[i]);
  }

  const holes = [];
  const strokes = [];

  for (const cluster of Object.values(clusters)) {
    if (cluster.length === 1) {
      holes.push(cluster[0].closedHole);
      strokes.push(cluster[0].drawnStroke);
    } else {
      let unioned = [cluster[0].closedHole.map(([lat, lng]) => [lng, lat])];
      for (let k = 1; k < cluster.length; k++) {
        const nextPoly = [cluster[k].closedHole.map(([lat, lng]) => [lng, lat])];
        try {
          unioned = union(unioned, nextPoly);
        } catch (err) {
          console.warn('polyclip union warning:', err);
        }
      }

      unioned.forEach(poly => {
        if (poly && poly[0] && poly[0].length > 2) {
          const outerRing = poly[0].map(([lng, lat]) => [lat, lng]);
          holes.push(outerRing);
          // Overlap by 2 points so pen stroke closes naturally
          const strokeRing = [...outerRing, outerRing[1] || outerRing[0]];
          strokes.push(strokeRing);
        }
      });
    }
  }

  return { holes, strokes };
}

